import { useCallback, useEffect, useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import { motion } from "framer-motion";
import { toast } from "sonner";
import api, { formatApiErrorDetail, getToken } from "@/lib/api";
import {
  ShieldCheck, Gauge, Camera, Video, Sparkles, MapPin, Clock, FileCheck2, Download,
  AlertTriangle, CheckCircle2, Send, X, Loader2, HardDrive, Fingerprint, Satellite,
  Timer, User, ClipboardList, Radar,
} from "lucide-react";

const bandColor = (v) => (v >= 85 ? "#10b981" : v >= 70 ? "#22c55e" : v >= 50 ? "#f59e0b" : "#ef4444");

export function ScoreGauge({ value = 0, label, size = 84, primary = false }) {
  const v = Math.max(0, Math.min(100, Math.round(value || 0)));
  const r = 15.9155, circ = 2 * Math.PI * r;
  const color = bandColor(v);
  return (
    <div className="flex flex-col items-center gap-1" data-testid={`ev-gauge-${(label || "").replace(/\W+/g, "-").toLowerCase()}`}>
      <div className="relative" style={{ width: size, height: size }}>
        <svg viewBox="0 0 36 36" className="-rotate-90" style={{ width: size, height: size }}>
          <circle cx="18" cy="18" r={r} fill="none" stroke="#1e293b" strokeWidth={primary ? 3.2 : 2.6} />
          <motion.circle cx="18" cy="18" r={r} fill="none" stroke={color} strokeWidth={primary ? 3.2 : 2.6}
            strokeLinecap="round" initial={{ strokeDasharray: `0 ${circ}` }}
            animate={{ strokeDasharray: `${(v / 100) * circ} ${circ}` }} transition={{ duration: 0.9, ease: "easeOut" }} />
        </svg>
        <div className="absolute inset-0 flex items-center justify-center">
          <span className={`font-extrabold text-white ${primary ? "text-2xl" : "text-lg"}`}>{v}</span>
        </div>
      </div>
      {label && <span className="text-[10px] uppercase tracking-wider text-slate-400 text-center leading-tight max-w-[92px]">{label}</span>}
    </div>
  );
}

const Chip = ({ ok, children, testid }) => (
  <span data-testid={testid} className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-[11px] font-medium ${ok ? "bg-emerald-500/15 text-emerald-300" : "bg-slate-700/50 text-slate-400"}`}>
    {ok ? <CheckCircle2 className="w-3 h-3" /> : <AlertTriangle className="w-3 h-3" />} {children}
  </span>
);

// ---- lightweight fetch hook (polling + instant refresh)
export function useEvidenceIntel(caseId, { poll = false, ai = false } = {}) {
  const [intel, setIntel] = useState(undefined);
  const refresh = useCallback(async () => {
    if (!caseId) return;
    try { const { data } = await api.get(`/cases/${caseId}/evidence-intel${ai ? "?ai=true" : ""}`); setIntel(data); }
    catch { setIntel(null); }
  }, [caseId, ai]);
  useEffect(() => { refresh(); }, [refresh]);
  useEffect(() => { if (!poll || !caseId) return; const t = setInterval(refresh, 12000); return () => clearInterval(t); }, [poll, caseId, refresh]);
  return { intel, refresh };
}

// ================================================================= Compact badge
export function EvidenceIntelBadge({ caseId, dark = false }) {
  const { intel } = useEvidenceIntel(caseId);
  if (!intel || !intel.has_evidence) return null;
  const s = intel.scores.evidence_capture_score;
  const c = bandColor(s);
  return (
    <div data-testid={`ev-badge-${caseId}`} className={`inline-flex items-center gap-2 px-3 py-1.5 rounded-xl ${dark ? "bg-slate-900/60 border border-slate-800" : "bg-white border border-slate-200"}`}>
      <Radar className="w-3.5 h-3.5" style={{ color: c }} />
      <span className={`text-xs font-semibold ${dark ? "text-slate-200" : "text-navy"}`}>Evidence</span>
      <span className="text-sm font-extrabold" style={{ color: c }}>{s}</span>
      {intel.missing_count > 0 && <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-amber-500/15 text-amber-500">{intel.missing_count} missing</span>}
    </div>
  );
}

// ================================================================= Request modal
function RequestEvidenceModal({ caseId, missing, onClose, onDone }) {
  const [selected, setSelected] = useState(() => new Set());
  const [priority, setPriority] = useState("normal");
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);
  const flat = useMemo(() => missing.flatMap((g) => g.items.map((i) => ({ ...i, folder: g.folder }))), [missing]);
  const toggle = (id) => setSelected((s) => { const n = new Set(s); n.has(id) ? n.delete(id) : n.add(id); return n; });
  const submit = async () => {
    const items = flat.filter((i) => selected.has(i.item_id || i.label)).map((i) => ({ item_id: i.item_id, label: i.label }));
    if (!items.length) { toast.error("Select at least one item to request."); return; }
    setBusy(true);
    try {
      await api.post(`/cases/${caseId}/evidence-requests`, { items, priority, note });
      toast.success(`Requested ${items.length} item(s) — pilot notified.`);
      onDone?.(); onClose();
    } catch (e) { toast.error(formatApiErrorDetail(e?.response?.data?.detail)); } finally { setBusy(false); }
  };
  return (
    <div className="fixed inset-0 z-[70] bg-black/60 flex items-center justify-center p-4" data-testid="ev-request-modal">
      <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} className="bg-navy border border-slate-800 rounded-3xl w-full max-w-lg max-h-[85vh] overflow-hidden flex flex-col text-slate-100">
        <div className="flex items-center justify-between px-5 py-4 border-b border-slate-800">
          <h3 className="font-heading font-bold flex items-center gap-2"><ClipboardList className="w-5 h-5 text-cyan-400" /> Request Additional Evidence™</h3>
          <button onClick={onClose} className="p-1.5 rounded-full hover:bg-slate-800"><X className="w-5 h-5" /></button>
        </div>
        <div className="p-5 overflow-y-auto space-y-4">
          <div>
            <div className="text-xs text-slate-400 mb-1.5">Select missing items to request from the pilot</div>
            <div className="space-y-3">
              {missing.map((g) => (
                <div key={g.folder}>
                  <div className="text-[11px] uppercase tracking-wide text-cyan-300 mb-1">{g.folder}</div>
                  <div className="space-y-1">
                    {g.items.map((i) => {
                      const id = i.item_id || i.label;
                      return (
                        <label key={id} data-testid={`ev-req-item-${id}`} className="flex items-center gap-2 rounded-lg border border-slate-800 bg-slate-900/50 px-3 py-2 cursor-pointer hover:border-cyan-500/40">
                          <input type="checkbox" checked={selected.has(id)} onChange={() => toggle(id)} className="accent-cyan-500" />
                          <span className="text-sm text-slate-200">{i.label}</span>
                        </label>
                      );
                    })}
                  </div>
                </div>
              ))}
            </div>
          </div>
          <label className="block"><span className="text-xs text-slate-400">Priority</span>
            <select value={priority} onChange={(e) => setPriority(e.target.value)} data-testid="ev-req-priority" className="mt-1 w-full bg-slate-900 border border-slate-700 rounded-lg px-3 py-2 text-sm outline-none focus:border-cyan-500">
              <option value="low">Low</option><option value="normal">Normal</option><option value="high">High</option><option value="urgent">Urgent</option>
            </select></label>
          <label className="block"><span className="text-xs text-slate-400">Note to pilot (optional)</span>
            <textarea value={note} onChange={(e) => setNote(e.target.value)} rows={2} data-testid="ev-req-note" className="mt-1 w-full bg-slate-900 border border-slate-700 rounded-lg px-3 py-2 text-sm outline-none focus:border-cyan-500" placeholder="e.g. focus on the skid marks near the north crosswalk" /></label>
        </div>
        <div className="px-5 py-4 border-t border-slate-800 flex justify-end gap-2">
          <button onClick={onClose} className="px-4 py-2 rounded-full bg-slate-800 text-slate-300 text-sm">Cancel</button>
          <button onClick={submit} disabled={busy} data-testid="ev-req-submit" className="flex items-center gap-2 px-5 py-2 rounded-full bg-cyan-500 text-slate-950 text-sm font-semibold hover:bg-cyan-400 disabled:opacity-50">{busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />} Send request</button>
        </div>
      </motion.div>
    </div>
  );
}

// ================================================================= Full panel
export function EvidenceIntelPanel({ caseId: propCaseId, canRequest = true }) {
  const params = useParams();
  const caseId = propCaseId || params.caseId;
  const { intel, refresh } = useEvidenceIntel(caseId, { poll: true, ai: true });
  const [showReq, setShowReq] = useState(false);
  const [genBusy, setGenBusy] = useState(false);
  const token = getToken();

  if (intel === undefined)
    return <div className="mt-8 rounded-3xl border border-slate-800 bg-navy p-8 flex justify-center"><Loader2 className="w-6 h-6 text-cyan-400 animate-spin" /></div>;
  if (!intel || !intel.has_evidence)
    return (
      <div className="mt-8 rounded-3xl border border-slate-800 bg-navy text-slate-300 p-6" data-testid="ev-intel-empty">
        <p className="text-xs font-medium text-cyan-400 uppercase tracking-widest flex items-center gap-1.5"><Radar className="w-3.5 h-3.5" /> Premium Evidence Intelligence™</p>
        <h2 className="font-heading text-2xl font-extrabold text-white mt-1">No drone evidence yet</h2>
        <p className="text-sm text-slate-400 mt-1">Once a Response Network™ pilot documents the scene, live evidence intelligence and the Evidence Quality Certificate™ appear here automatically.</p>
      </div>
    );

  const s = intel.scores, v = intel.verification, t = intel.totals, rd = intel.readiness;
  const readinessList = [["Insurance", rd.insurance], ["Demand", rd.demand], ["Reconstruction", rd.reconstruction], ["Litigation", rd.litigation], ["Trial", rd.trial]];
  const certUrl = intel.certificate_id ? `${process.env.REACT_APP_BACKEND_URL}/api/cases/${caseId}/evidence-certificate?auth=${token}` : null;

  const generateCert = async () => {
    setGenBusy(true);
    try { await api.post(`/cases/${caseId}/evidence-certificate`, {}); toast.success("Evidence Quality Certificate™ generated"); refresh(); }
    catch (e) { toast.error(formatApiErrorDetail(e?.response?.data?.detail)); } finally { setGenBusy(false); }
  };

  return (
    <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.4 }}
      className="mt-8 rounded-3xl border border-slate-800 bg-navy text-slate-100 p-6" data-testid="ev-intel-panel">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <p className="text-xs font-medium text-cyan-400 uppercase tracking-widest flex items-center gap-1.5"><Radar className="w-3.5 h-3.5" /> Premium Evidence Intelligence™</p>
          <h2 className="font-heading text-2xl font-extrabold text-white mt-1">Live Evidence Engine</h2>
        </div>
        <div className="flex items-center gap-3">
          <span className={`text-[11px] px-2.5 py-1 rounded-full font-semibold ${intel.status === "completed" ? "bg-emerald-500/15 text-emerald-300" : "bg-amber-500/15 text-amber-300"}`} data-testid="ev-intel-status">
            {intel.status === "completed" ? "Mission complete" : intel.status === "in_progress" ? "Capturing live" : "Evidence"}</span>
          <ScoreGauge value={s.evidence_capture_score} label="Capture Score™" size={78} primary />
        </div>
      </div>

      {/* Primary score gauges */}
      <div className="mt-5 grid grid-cols-3 sm:grid-cols-6 gap-3 justify-items-center rounded-2xl bg-slate-900/50 border border-slate-800 p-4" data-testid="ev-intel-scores">
        <ScoreGauge value={s.court_readiness} label="Court Readiness™" />
        <ScoreGauge value={s.crash_reconstruction_readiness} label="Reconstruction™" />
        <ScoreGauge value={s.insurance_documentation} label="Insurance Doc™" />
        <ScoreGauge value={s.ai_evidence_quality} label="AI Quality™" />
        <ScoreGauge value={s.evidence_completeness} label="Completeness" />
        <ScoreGauge value={s.scene_documentation} label="Scene Doc" />
      </div>

      {/* Verification + totals */}
      <div className="mt-4 grid md:grid-cols-2 gap-3">
        <div className="rounded-2xl bg-slate-900/50 border border-slate-800 p-4">
          <div className="text-xs font-semibold text-slate-400 uppercase tracking-wide mb-2 flex items-center gap-1.5"><Fingerprint className="w-3.5 h-3.5" /> Chain of Custody &amp; Verification</div>
          <div className="flex flex-wrap gap-2">
            <Chip ok={v.chain_of_custody.status === "verified"} testid="ev-coc">Chain of custody: {v.chain_of_custody.status}</Chip>
            <Chip ok={v.gps_verified} testid="ev-gps"><Satellite className="w-3 h-3" /> GPS</Chip>
            <Chip ok={v.timestamp_verified} testid="ev-timestamp"><Clock className="w-3 h-3" /> Timestamp</Chip>
            <Chip ok={v.metadata_verified} testid="ev-metadata"><FileCheck2 className="w-3 h-3" /> Metadata {v.metadata_pct}%</Chip>
          </div>
        </div>
        <div className="rounded-2xl bg-slate-900/50 border border-slate-800 p-4 grid grid-cols-3 gap-y-3 text-center">
          {[[Camera, t.total_photos, "Photos"], [Video, t.total_videos, "Videos"], [HardDrive, `${t.total_size_mb}MB`, "Size"],
            [Timer, t.mission_duration_min ? `${t.mission_duration_min}m` : "—", "Mission"], [User, t.uploaded_by || "—", "By"],
            [Clock, t.completion_time ? new Date(t.completion_time).toLocaleDateString() : "—", "Completed"]].map(([I, val, lbl], i) => (
            <div key={i} className="flex flex-col items-center">
              <I className="w-4 h-4 text-cyan-400" />
              <div className="text-sm font-bold text-white mt-1 truncate max-w-[90px]">{val}</div>
              <div className="text-[10px] uppercase tracking-wide text-slate-500">{lbl}</div>
            </div>
          ))}
        </div>
      </div>

      {/* Readiness flags */}
      <div className="mt-4 flex flex-wrap gap-2" data-testid="ev-readiness">
        {readinessList.map(([l, ok]) => (
          <span key={l} className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-semibold ${ok ? "bg-emerald-500/15 text-emerald-300" : "bg-slate-800 text-slate-500"}`} data-testid={`ev-ready-${l.toLowerCase()}`}>
            {ok ? <CheckCircle2 className="w-3.5 h-3.5" /> : <Clock className="w-3.5 h-3.5" />} Ready for {l}
          </span>
        ))}
      </div>

      {/* AI assessment */}
      {intel.ai_assessment && (
        <div className="mt-4 rounded-2xl bg-gradient-to-br from-cyan-500/10 to-slate-900/40 border border-cyan-500/20 p-4" data-testid="ev-ai-assessment">
          <div className="text-xs font-semibold text-cyan-300 uppercase tracking-wide mb-1 flex items-center gap-1.5"><Sparkles className="w-3.5 h-3.5" /> Overall AI Assessment</div>
          <p className="text-sm text-slate-200 leading-relaxed">{intel.ai_assessment}</p>
        </div>
      )}

      {/* Missing checklist + request */}
      {intel.missing_count > 0 ? (
        <div className="mt-4 rounded-2xl bg-slate-900/50 border border-amber-500/20 p-4" data-testid="ev-missing-checklist">
          <div className="flex items-center justify-between flex-wrap gap-2">
            <div className="text-sm font-semibold text-amber-300 flex items-center gap-1.5"><AlertTriangle className="w-4 h-4" /> Missing Evidence Checklist™ ({intel.missing_count})</div>
            {canRequest && (
              <button onClick={() => setShowReq(true)} data-testid="ev-request-open" className="flex items-center gap-1.5 px-4 py-2 rounded-full bg-cyan-500 text-slate-950 text-sm font-semibold hover:bg-cyan-400">
                <ClipboardList className="w-4 h-4" /> Request Additional Evidence
              </button>
            )}
          </div>
          <div className="mt-2 grid sm:grid-cols-2 gap-x-6 gap-y-1">
            {intel.missing_checklist.flatMap((g) => g.items.map((i) => (
              <div key={g.folder + i.label} className="text-xs text-slate-400 flex items-center gap-1.5"><span className="w-1.5 h-1.5 rounded-full bg-amber-400" /> {i.label} <span className="text-slate-600">· {g.folder}</span></div>
            )))}
          </div>
          {intel.open_requests > 0 && <div className="mt-2 text-xs text-cyan-300">{intel.open_requests} open request(s) awaiting the pilot.</div>}
        </div>
      ) : (
        <div className="mt-4 text-sm text-emerald-300 flex items-center gap-2" data-testid="ev-complete-notice"><CheckCircle2 className="w-5 h-5" /> All required evidence documented.</div>
      )}

      {/* Certificate */}
      <div className="mt-4 flex items-center gap-2 flex-wrap">
        {certUrl ? (
          <a href={certUrl} target="_blank" rel="noreferrer" data-testid="ev-cert-download" className="flex items-center gap-2 px-5 py-2.5 rounded-full bg-emerald-500 text-slate-950 text-sm font-semibold hover:bg-emerald-400"><Download className="w-4 h-4" /> Evidence Quality Certificate™ (PDF)</a>
        ) : intel.status === "completed" ? (
          <button onClick={generateCert} disabled={genBusy} data-testid="ev-cert-generate" className="flex items-center gap-2 px-5 py-2.5 rounded-full bg-navy border border-emerald-500/40 text-emerald-300 text-sm font-semibold hover:bg-emerald-500/10 disabled:opacity-50">{genBusy ? <Loader2 className="w-4 h-4 animate-spin" /> : <FileCheck2 className="w-4 h-4" />} Generate Certificate</button>
        ) : (
          <span className="text-xs text-slate-500 flex items-center gap-1.5"><FileCheck2 className="w-3.5 h-3.5" /> Certificate generates automatically when the mission completes.</span>
        )}
      </div>

      {showReq && <RequestEvidenceModal caseId={caseId} missing={intel.missing_checklist} onClose={() => setShowReq(false)} onDone={refresh} />}
    </motion.div>
  );
}

// ================================================================= Firm/Attorney dashboard widget
export function PremiumEvidenceWidget({ dark = false }) {
  const [d, setD] = useState(undefined);
  useEffect(() => {
    const load = () => api.get("/attorney/evidence-intel/summary").then((r) => setD(r.data)).catch(() => setD(null));
    load(); const t = setInterval(load, 20000); return () => clearInterval(t);
  }, []);
  if (d === undefined) return <div className={`rounded-3xl p-6 flex justify-center ${dark ? "bg-navy border border-slate-800" : "bg-white border border-slate-100"}`}><Loader2 className={`w-5 h-5 animate-spin ${dark ? "text-cyan-400" : "text-cobalt"}`} /></div>;
  if (!d || d.total_documented_cases === 0)
    return (
      <div className={`rounded-3xl p-6 ${dark ? "bg-navy border border-slate-800 text-slate-300" : "bg-white border border-slate-100"}`} data-testid="ev-widget-empty">
        <p className={`text-xs font-medium uppercase tracking-widest flex items-center gap-1.5 ${dark ? "text-cyan-400" : "text-cobalt"}`}><Radar className="w-3.5 h-3.5" /> Premium Evidence Intelligence™</p>
        <p className={`mt-2 text-sm ${dark ? "text-slate-400" : "text-slate-500"}`}>No documented cases yet. Dispatch a drone pilot to start building courtroom-ready evidence packages.</p>
      </div>
    );
  const txt = dark ? "text-white" : "text-navy", muted = dark ? "text-slate-400" : "text-slate-500";
  const card = dark ? "bg-slate-900/50 border-slate-800" : "bg-slate-50 border-slate-100";
  const ready = d.ready || {};
  return (
    <div className={`rounded-3xl p-6 ${dark ? "bg-navy border border-slate-800" : "bg-white border border-slate-100 shadow-[0_8px_30px_rgb(0,0,0,0.05)]"}`} data-testid="ev-widget">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <p className={`text-xs font-medium uppercase tracking-widest flex items-center gap-1.5 ${dark ? "text-cyan-400" : "text-cobalt"}`}><Radar className="w-3.5 h-3.5" /> Premium Evidence Intelligence™</p>
        {d.open_requests > 0 && <span className="text-[11px] px-2 py-0.5 rounded-full bg-amber-500/15 text-amber-500">{d.open_requests} open request(s)</span>}
      </div>
      <div className="mt-4 grid grid-cols-2 sm:grid-cols-4 gap-3">
        {[["Avg Evidence Score", d.avg_evidence_score, "text-cyan-400"], ["Documented Cases", d.total_documented_cases, txt],
          ["Avg Mission", d.avg_mission_min ? `${d.avg_mission_min}m` : "—", txt], ["Cases Missing Evidence", d.cases_missing_evidence.length, "text-amber-500"]].map(([l, val, c]) => (
          <div key={l} className={`rounded-2xl border p-3 ${card}`}>
            <div className={`text-[11px] uppercase tracking-wide ${muted}`}>{l}</div>
            <div className={`mt-1 text-2xl font-extrabold ${c}`}>{val}</div>
          </div>
        ))}
      </div>
      <div className="mt-3 grid grid-cols-2 sm:grid-cols-5 gap-2">
        {[["Demand", ready.demand], ["Reconstruction", ready.reconstruction], ["Litigation", ready.litigation], ["Insurance", ready.insurance], ["Trial", ready.trial]].map(([l, n]) => (
          <div key={l} className={`rounded-xl border p-2.5 text-center ${card}`} data-testid={`ev-widget-ready-${l.toLowerCase()}`}>
            <div className={`text-lg font-extrabold ${n > 0 ? "text-emerald-400" : muted}`}>{n || 0}</div>
            <div className={`text-[10px] uppercase tracking-wide ${muted}`}>Ready · {l}</div>
          </div>
        ))}
      </div>
      <div className="mt-4 grid md:grid-cols-2 gap-4">
        <div>
          <div className={`text-xs font-semibold uppercase tracking-wide mb-1.5 ${muted}`}>Evidence Quality Ranking</div>
          <div className="space-y-1">
            {d.quality_ranking.slice(0, 5).map((c) => (
              <div key={c.case_id} className={`flex items-center justify-between text-sm rounded-lg px-2.5 py-1.5 border ${card}`} data-testid={`ev-rank-${c.case_id}`}>
                <span className={`truncate ${dark ? "text-slate-200" : "text-slate-700"}`}>{c.case_number || c.title || c.case_id.slice(0, 8)}</span>
                <span className="font-extrabold" style={{ color: bandColor(c.score) }}>{c.score}</span>
              </div>
            ))}
          </div>
        </div>
        <div>
          <div className={`text-xs font-semibold uppercase tracking-wide mb-1.5 ${muted}`}>Cases Missing Evidence</div>
          {d.cases_missing_evidence.length === 0 ? <p className={`text-sm ${muted}`}>All documented cases are complete.</p> : (
            <div className="space-y-1">
              {d.cases_missing_evidence.slice(0, 5).map((c) => (
                <div key={c.case_id} className={`flex items-center justify-between text-sm rounded-lg px-2.5 py-1.5 border ${card}`}>
                  <span className={`truncate ${dark ? "text-slate-200" : "text-slate-700"}`}>{c.case_number || c.case_id.slice(0, 8)}</span>
                  <span className="text-xs text-amber-500">{c.missing_count} missing</span>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
      {(d.most_active_pilot || d.most_requested_evidence) && (
        <div className={`mt-4 pt-3 border-t ${dark ? "border-slate-800" : "border-slate-100"} flex flex-wrap gap-x-6 gap-y-1 text-xs ${muted}`}>
          {d.most_active_pilot && <span>Most active pilot: <b className={dark ? "text-slate-200" : "text-navy"}>{d.most_active_pilot.name}</b> ({d.most_active_pilot.missions} missions)</span>}
          {d.most_requested_evidence && <span>Most requested: <b className={dark ? "text-slate-200" : "text-navy"}>{d.most_requested_evidence}</b></span>}
        </div>
      )}
    </div>
  );
}
